import QrScanner from "qr-scanner";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";

export type ScannerJsqrPreprocessProps = {
	onResult: (result: { data: string }) => void;
	className?: string;
};

export type ScannerJsqrPreprocessRef = {
	switchCamera: () => Promise<void>;
	hasMultipleCameras: () => Promise<boolean>;
	toggleTorch: () => Promise<void>;
};

export const ScannerJsqrPreprocess = forwardRef<ScannerJsqrPreprocessRef, ScannerJsqrPreprocessProps>((props, forwardedRef) => {
	const { onResult, className } = props;
	const onResultRef = useRef(onResult);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const animationFrameRef = useRef<number | null>(null);
	const lastScanTimeRef = useRef<number>(0);
	const currentFacingModeRef = useRef<"environment" | "user">("environment");
	const torchEnabledRef = useRef(false);

	const SCAN_INTERVAL_MS = 100;

	const stopStream = useCallback(() => {
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		}
	}, []);

	const applyBinarization = useCallback((ctx: CanvasRenderingContext2D, width: number, height: number) => {
		const imageData = ctx.getImageData(0, 0, width, height);
		const data = imageData.data;

		// First pass: compute grayscale histogram
		const histogram = new Uint32Array(256);
		const grayValues = new Uint8Array(data.length / 4);
		for (let i = 0, j = 0; i < data.length; i += 4, j++) {
			const gray = Math.round(data[i]! * 0.299 + data[i + 1]! * 0.587 + data[i + 2]! * 0.114);
			grayValues[j] = gray;
			histogram[gray] = histogram[gray]! + 1;
		}

		// Otsu's method: find optimal threshold
		const totalPixels = width * height;
		let sumTotal = 0;
		for (let i = 0; i < 256; i++) {
			sumTotal += i * histogram[i]!;
		}

		let sumBackground = 0;
		let weightBackground = 0;
		let maxVariance = 0;
		let threshold = 128; // fallback

		for (let t = 0; t < 256; t++) {
			weightBackground += histogram[t]!;
			if (weightBackground === 0) continue;

			const weightForeground = totalPixels - weightBackground;
			if (weightForeground === 0) break;

			sumBackground += t * histogram[t]!;

			const meanBackground = sumBackground / weightBackground;
			const meanForeground = (sumTotal - sumBackground) / weightForeground;

			const betweenVariance = weightBackground * weightForeground * (meanBackground - meanForeground) ** 2;

			if (betweenVariance > maxVariance) {
				maxVariance = betweenVariance;
				threshold = t;
			}
		}

		// Second pass: apply threshold
		for (let i = 0, j = 0; i < data.length; i += 4, j++) {
			const val = grayValues[j]! > threshold ? 255 : 0;
			data[i] = val;
			data[i + 1] = val;
			data[i + 2] = val;
		}

		ctx.putImageData(imageData, 0, 0);
	}, []);

	const startCamera = useCallback(
		async (facingMode: "environment" | "user") => {
			stopStream();

			const stream = await navigator.mediaDevices.getUserMedia({
				video: {
					width: { ideal: 1920 },
					height: { ideal: 1080 },
					facingMode,
				},
				audio: false,
			});

			streamRef.current = stream;
			currentFacingModeRef.current = facingMode;

			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				await videoRef.current.play();
			}
		},
		[stopStream]
	);

	const scanFrame = useCallback(() => {
		const video = videoRef.current;
		const canvas = canvasRef.current;

		if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
			animationFrameRef.current = requestAnimationFrame(scanFrame);
			return;
		}

		const now = performance.now();
		if (now - lastScanTimeRef.current < SCAN_INTERVAL_MS) {
			animationFrameRef.current = requestAnimationFrame(scanFrame);
			return;
		}
		lastScanTimeRef.current = now;

		const ctx = canvas.getContext("2d", { willReadFrequently: true });
		if (!ctx) {
			animationFrameRef.current = requestAnimationFrame(scanFrame);
			return;
		}

		const width = video.videoWidth;
		const height = video.videoHeight;

		if (canvas.width !== width || canvas.height !== height) {
			canvas.width = width;
			canvas.height = height;
		}

		ctx.drawImage(video, 0, 0, width, height);
		applyBinarization(ctx, width, height);

		QrScanner.scanImage(canvas, { returnDetailedScanResult: true })
			.then((result) => {
				onResultRef.current({ data: result.data });
			})
			.catch(() => {
				// No QR code found, continue scanning
			});

		animationFrameRef.current = requestAnimationFrame(scanFrame);
	}, [applyBinarization]);

	useImperativeHandle(forwardedRef, () => ({
		switchCamera: async () => {
			const newFacingMode = currentFacingModeRef.current === "environment" ? "user" : "environment";
			await startCamera(newFacingMode);
			animationFrameRef.current = requestAnimationFrame(scanFrame);
		},
		hasMultipleCameras: async () => {
			const cameras = await QrScanner.listCameras(true);
			return cameras.length > 1;
		},
		toggleTorch: async () => {
			if (!streamRef.current) return;

			const track = streamRef.current.getVideoTracks()[0];
			if (!track) return;

			const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
			if (!capabilities.torch) return;

			torchEnabledRef.current = !torchEnabledRef.current;
			await track.applyConstraints({
				advanced: [{ torch: torchEnabledRef.current } as MediaTrackConstraintSet],
			});
		},
	}));

	useEffect(() => {
		onResultRef.current = onResult;
	}, [onResult]);

	const ref = useCallback(
		(el: HTMLVideoElement | null) => {
			if (!el) {
				stopStream();
				videoRef.current = null;
				return;
			}

			videoRef.current = el;

			if (!canvasRef.current) {
				canvasRef.current = document.createElement("canvas");
			}

			startCamera("environment")
				.then(() => {
					animationFrameRef.current = requestAnimationFrame(scanFrame);
				})
				.catch((error) => {
					console.error("Failed to start camera:", error);
				});
		},
		[startCamera, stopStream, scanFrame]
	);

	return <video ref={ref} className={className} playsInline muted />;
});
